import { useState } from "react";
import { useTheme, DARK, LIGHT } from "../../components/Layout";
import { hrAPI } from "../../services/api";
import PersianDatePicker from "../../utils/PersianDatePicker";
import { UserPlus, Save } from "lucide-react";

interface Props { onSuccess: () => void; }

const departments = ["اداری","مالی","فروش","خرید","گدام","تولید","IT","خدمات"];
const educations  = ["ابتدایی","متوسطه","لیسه","دیپلم","لیسانس","ماستری","دکتورا"];

export default function AddEmployee({ onSuccess }: Props) {
  const { dark } = useTheme();
  const T = dark ? DARK : LIGHT;
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState("");
  const [step, setStep] = useState(1);

  const [form, setForm] = useState({
    first_name: "", last_name: "", father_name: "",
    national_id: "", birth_date: "", gender: "male",
    phone: "", phone2: "", address: "", province: "",
    position: "", department: "", education: "",
    experience: "", notes: "",
  });

  const set = (k: string, v: string) => setForm(f => ({ ...f, [k]: v }));

  const showToast = (msg: string) => { setToast(msg); setTimeout(() => setToast(""), 3500); };

  const handleSubmit = async () => {
    if (!form.first_name || !form.last_name) { showToast("❌ نام و تخلص الزامی است"); return; }
    if (!form.phone) { showToast("❌ شماره تماس الزامی است"); return; }
    setSaving(true);
    try {
      const r = await hrAPI.employeeAdd(form);
      if (r.success) { showToast("✅ " + r.message); setTimeout(onSuccess, 1000); }
      else showToast("❌ " + r.message);
    } catch { showToast("❌ خطا در اتصال به سرور"); }
    setSaving(false);
  };

  const card: React.CSSProperties = {
    borderRadius: 16, padding: 28,
    background: dark ? "rgba(255,255,255,0.03)" : "rgba(255,255,255,0.95)",
    border: `1px solid ${T.cardBorder}`,
  };

  const inp: React.CSSProperties = {
    width: "100%", padding: "10px 14px",
    background: dark ? "rgba(255,255,255,0.05)" : "#f8faff",
    border: `1.5px solid ${dark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)"}`,
    borderRadius: 10, color: T.text, fontSize: 13,
    fontFamily: "'Vazirmatn','Segoe UI',sans-serif",
    direction: "rtl", outline: "none", boxSizing: "border-box" as const,
  };

  const lbl: React.CSSProperties = {
    fontSize: 11, fontWeight: 700, color: T.subtext,
    display: "block", marginBottom: 6,
  };

  return (
    <div dir="rtl">
      {toast && (
        <div style={{ position: "fixed", top: 20, left: "50%", transform: "translateX(-50%)", zIndex: 999, padding: "12px 24px", borderRadius: 12, background: dark ? "#1e293b" : "#fff", border: `1px solid ${T.cardBorder}`, boxShadow: "0 8px 32px rgba(0,0,0,0.3)", color: T.text, fontSize: 13, fontWeight: 600, fontFamily: "'Vazirmatn','Segoe UI',sans-serif" }}>
          {toast}
        </div>
      )}

      {/* Steps */}
      <div style={{ display: "flex", gap: 8, marginBottom: 24 }}>
        {[{ n: 1, label: "مشخصات شخصی" }, { n: 2, label: "اطلاعات تماس" }, { n: 3, label: "اطلاعات شغلی" }].map(s => (
          <div key={s.n} onClick={() => setStep(s.n)} style={{ flex: 1, padding: "10px 16px", borderRadius: 10, cursor: "pointer", textAlign: "center", fontSize: 13, fontWeight: 700, background: step === s.n ? "linear-gradient(135deg,#8b5cf6,#6d28d9)" : dark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.05)", color: step === s.n ? "white" : T.subtext, transition: "all 0.2s" }}>
            <span style={{ marginLeft: 6, width: 22, height: 22, borderRadius: "50%", background: step === s.n ? "rgba(255,255,255,0.2)" : "transparent", display: "inline-flex", alignItems: "center", justifyContent: "center" }}>{s.n}</span>
            {s.label}
          </div>
        ))}
      </div>

      <div style={card}>
        {/* مرحله ۱ */}
        {step === 1 && (
          <div>
            <h3 style={{ margin: "0 0 20px", fontSize: 15, fontWeight: 800, color: T.text }}>👤 مشخصات شخصی</h3>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16 }}>
              <div>
                <label style={lbl}>نام *</label>
                <input value={form.first_name} onChange={e => set("first_name", e.target.value)} placeholder="نام" style={inp} />
              </div>
              <div>
                <label style={lbl}>تخلص *</label>
                <input value={form.last_name} onChange={e => set("last_name", e.target.value)} placeholder="تخلص" style={inp} />
              </div>
              <div>
                <label style={lbl}>نام پدر</label>
                <input value={form.father_name} onChange={e => set("father_name", e.target.value)} placeholder="نام پدر" style={inp} />
              </div>
              <div>
                <label style={lbl}>شماره تذکره</label>
                <input value={form.national_id} onChange={e => set("national_id", e.target.value)} placeholder="شماره تذکره" style={inp} />
              </div>
              <div>
                <PersianDatePicker value={form.birth_date} onChange={v => set("birth_date", v)} label="تاریخ تولد" />
              </div>
              <div>
                <label style={lbl}>جنسیت</label>
                <select value={form.gender} onChange={e => set("gender", e.target.value)} style={{ ...inp, background: dark ? "#1e293b" : "#f8faff" }}>
                  <option value="male">مرد</option>
                  <option value="female">زن</option>
                </select>
              </div>
            </div>
          </div>
        )}

        {/* مرحله ۲ */}
        {step === 2 && (
          <div>
            <h3 style={{ margin: "0 0 20px", fontSize: 15, fontWeight: 800, color: T.text }}>📞 اطلاعات تماس</h3>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
              <div>
                <label style={lbl}>شماره تماس *</label>
                <input value={form.phone} onChange={e => set("phone", e.target.value)} placeholder="07xxxxxxxx" style={inp} />
              </div>
              <div>
                <label style={lbl}>شماره تماس دوم</label>
                <input value={form.phone2} onChange={e => set("phone2", e.target.value)} placeholder="07xxxxxxxx" style={inp} />
              </div>
              <div>
                <label style={lbl}>ولایت</label>
                <input value={form.province} onChange={e => set("province", e.target.value)} placeholder="نام ولایت..." style={inp} />
              </div>
              <div>
                <label style={lbl}>آدرس</label>
                <input value={form.address} onChange={e => set("address", e.target.value)} placeholder="آدرس کامل" style={inp} />
              </div>
            </div>
          </div>
        )}

        {/* مرحله ۳ */}
        {step === 3 && (
          <div>
            <h3 style={{ margin: "0 0 20px", fontSize: 15, fontWeight: 800, color: T.text }}>💼 اطلاعات شغلی</h3>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
              <div>
                <label style={lbl}>وظیفه / پست</label>
                <input value={form.position} onChange={e => set("position", e.target.value)} placeholder="مثال: مدیر فروش" style={inp} />
              </div>
              <div>
                <label style={lbl}>بخش / دیپارتمنت</label>
                <select value={form.department} onChange={e => set("department", e.target.value)} style={{ ...inp, background: dark ? "#1e293b" : "#f8faff" }}>
                  <option value="">انتخاب بخش...</option>
                  {departments.map(d => <option key={d} value={d}>{d}</option>)}
                </select>
              </div>
              <div>
                <label style={lbl}>سطح تحصیلات</label>
                <select value={form.education} onChange={e => set("education", e.target.value)} style={{ ...inp, background: dark ? "#1e293b" : "#f8faff" }}>
                  <option value="">انتخاب...</option>
                  {educations.map(e => <option key={e} value={e}>{e}</option>)}
                </select>
              </div>
              <div>
                <label style={lbl}>تجربه کاری</label>
                <input value={form.experience} onChange={e => set("experience", e.target.value)} placeholder="مثال: ۵ سال تجربه در فروش" style={inp} />
              </div>
              <div style={{ gridColumn: "1/-1" }}>
                <label style={lbl}>یادداشت</label>
                <textarea value={form.notes} onChange={e => set("notes", e.target.value)} placeholder="یادداشت‌های اضافی..." rows={3} style={{ ...inp, resize: "vertical" }} />
              </div>
            </div>
          </div>
        )}

        {/* دکمه‌ها */}
        <div style={{ display: "flex", gap: 10, marginTop: 24, justifyContent: "space-between" }}>
          <div style={{ display: "flex", gap: 8 }}>
            {step > 1 && (
              <button onClick={() => setStep(step - 1)} style={{ padding: "10px 20px", borderRadius: 10, border: `1px solid ${T.cardBorder}`, background: "transparent", color: T.text, fontSize: 13, cursor: "pointer", fontFamily: "inherit", fontWeight: 600 }}>
                ← قبلی
              </button>
            )}
            {step < 3 && (
              <button onClick={() => setStep(step + 1)} style={{ padding: "10px 20px", borderRadius: 10, border: "none", background: "linear-gradient(135deg,#8b5cf6,#6d28d9)", color: "white", fontSize: 13, cursor: "pointer", fontFamily: "inherit", fontWeight: 700 }}>
                بعدی ←
              </button>
            )}
          </div>
          {step === 3 && (
            <button onClick={handleSubmit} disabled={saving} style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 28px", borderRadius: 10, border: "none", background: saving ? "#64748b" : "linear-gradient(135deg,#8b5cf6,#6d28d9)", color: "white", fontSize: 13, cursor: saving ? "not-allowed" : "pointer", fontFamily: "inherit", fontWeight: 700 }}>
              {saving ? "در حال ذخیره..." : <><Save size={15} /> ذخیره کارمند</>}
            </button>
          )}
        </div>
      </div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}